'use client';

import {
  formatLocationCaseText,
  formatLocationSlug,
  LOCATIONS_SEPARATOR,
  makeQueryString,
} from '@/helpers/common';
import { jobSearcherSuggestHandler } from '@/helpers/suggest-helper';
import { ROUTES } from '@/constants/routes/default';
import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';

type SearchSuggestion = { title: string };

const SUGGESTIONS_DEBOUNCE_MS = 250;

export type TSearchFilterParam = 'l' | 'occ' | 'ind' | 'sen';

export interface UseSearchFormProps {
  fromHomePage?: boolean;
  q?: string;
  l?: string;
  baseUrl?: string;
  queryParameters?: { [key: string]: string | undefined };
  scrollElementId?: string;
  locations?: string[];
  occupations?: string[];
  industries?: string[];
  seniorities?: string[];
}

const useSearchForm = ({
  fromHomePage,
  q,
  l,
  baseUrl = ROUTES.jobs,
  queryParameters,
  scrollElementId,
  locations = l ? [l] : [],
  occupations = [],
  industries = [],
  seniorities = [],
}: UseSearchFormProps) => {
  const { push } = useRouter();

  const singleLocation = locations.length === 1 ? locations[0] : '';
  const multipleLocations = locations.length > 1 ? locations : [];

  const filters: Record<TSearchFilterParam, string[]> = {
    l: locations,
    occ: occupations,
    ind: industries,
    sen: seniorities,
  };
  const filterParameters = {
    ...queryParameters,
    occ: occupations.join(','),
    ind: industries.join(','),
    sen: seniorities.join(','),
  };

  const getRemoveFilterHref = (param: TSearchFilterParam, code: string) =>
    `${baseUrl}${makeQueryString({
      q,
      l: locations.join(LOCATIONS_SEPARATOR),
      ...filterParameters,
      [param]: filters[param]
        .filter((item) => item !== code)
        .join(param === 'l' ? LOCATIONS_SEPARATOR : ','),
    })}`;

  const [keyword, setKeyword] = useState(q ?? '');
  const [location, setLocation] = useState(formatLocationCaseText(singleLocation));
  const [locationSuggestions, setLocationSuggestions] = useState<SearchSuggestion[]>([]);
  const [keywordSuggestions, setKeywordSuggestions] = useState<SearchSuggestion[]>([]);

  const [isKeywordsSuggestionsOpened, setIsKeywordsSuggestionsOpened] = useState(!keyword);
  const [isLocationsSuggestionsOpened, setIsLocationsSuggestionsOpened] = useState(false);
  const keywordRequestIdRef = useRef(0);
  const locationRequestIdRef = useRef(0);

  useEffect(() => {
    setLocation(formatLocationCaseText(singleLocation));
  }, [singleLocation]);

  const redirectFunction = ({ qValue, lValue }: { qValue?: string; lValue?: string }) => {
    if (!!qValue || (!qValue && q) || !!lValue || (!lValue && singleLocation) || fromHomePage) {
      push(
        `${baseUrl}${makeQueryString({
          q: qValue,
          l: lValue ? formatLocationSlug(lValue) : multipleLocations.join(LOCATIONS_SEPARATOR),
          ...filterParameters,
        })}`,
      );
    } else if (scrollElementId) {
      const targetElement = document ? document?.querySelector(scrollElementId) : null;
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement> | KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
    redirectFunction({ qValue: keyword, lValue: location });
  };

  const onFormKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      if (
        (isLocationsSuggestionsOpened && locationSuggestions.length > 0) ||
        (isKeywordsSuggestionsOpened && keywordSuggestions.length > 0)
      ) {
        event.preventDefault();
      } else {
        onSubmit(event);
      }
    }
  };

  useEffect(() => {
    const searchValue = keyword.trim();
    const requestId = ++keywordRequestIdRef.current;

    if (!searchValue) {
      setKeywordSuggestions([]);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      const { suggestions } = await jobSearcherSuggestHandler({ type: 'keyword', searchValue });

      if (requestId === keywordRequestIdRef.current) setKeywordSuggestions(suggestions);
    }, SUGGESTIONS_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [keyword]);

  useEffect(() => {
    const searchValue = location.trim();
    const requestId = ++locationRequestIdRef.current;

    if (!searchValue) {
      setLocationSuggestions([]);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      const { suggestions } = await jobSearcherSuggestHandler({ type: 'location', searchValue });

      if (requestId === locationRequestIdRef.current) setLocationSuggestions(suggestions);
    }, SUGGESTIONS_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [location]);

  const onKeywordChange = (event: FormEvent<Element>) => {
    const target = event?.target as HTMLInputElement;
    setKeyword(target?.value);
  };

  const onLocationChange = (event: FormEvent<Element>) => {
    const target = event?.target as HTMLInputElement;
    setLocation(target?.value);
  };

  const cancelKeywordSearch = () => {
    setKeyword('');
    setKeywordSuggestions([]);
    redirectFunction({
      qValue: '',
      lValue: location,
    });
  };

  const cancelLocationSearch = () => {
    setLocation('');
    setLocationSuggestions([]);
    redirectFunction({
      qValue: keyword,
      lValue: '',
    });
  };

  const onKeywordSuggestionsClick = (text: string) => {
    setKeyword(text);
    redirectFunction({ qValue: text, lValue: location });
  };

  const onLocationSuggestionsClick = (text: string) => {
    setLocation(text);
    redirectFunction({ qValue: keyword, lValue: text });
  };

  return {
    keyword,
    location,
    locationSuggestions,
    keywordSuggestions,
    isKeywordsSuggestionsOpened,
    isLocationsSuggestionsOpened,
    setIsKeywordsSuggestionsOpened,
    setIsLocationsSuggestionsOpened,
    onSubmit,
    onFormKeyDown,
    onKeywordChange,
    onLocationChange,
    cancelKeywordSearch,
    cancelLocationSearch,
    onKeywordSuggestionsClick,
    onLocationSuggestionsClick,
    multipleLocations,
    getRemoveFilterHref,
  };
};

export default useSearchForm;
